import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog } from '@/components/ui/Dialog'
import { getSignedUrl, openSignedPreview } from '@/lib/signedPreview'
import {
  closeImagePreview,
  useImagePreviewDoc,
  zoomByWheel,
  zoomIn,
  zoomLabel,
  zoomOut,
  MIN_ZOOM,
} from '@/lib/imagePreview'
import { pt } from '@/i18n/pt-PT'

// In-app image viewer with zoom. Mounted ONCE per shell (OperationsLayout for staff,
// PortalLayout for clients); shows whenever `openImagePreview` is called.
//
// Zoom is a CSS transform on the <img>, not a re-fetch: the signed URL is loaded once at
// natural resolution and scaled in the browser, so zooming costs nothing and never
// re-signs. The image src is a 10-minute signed URL held only in component state and
// dropped on close — it is never persisted, per the signed-URL red line.
export function ImagePreviewModal() {
  const doc = useImagePreviewDoc()
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [zoom, setZoom] = useState(MIN_ZOOM)
  // Pan offset in pixels, only meaningful while zoomed past fit.
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM)
    setPan({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    if (!doc) {
      setUrl(null)
      setError(null)
      resetView()
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setUrl(null)
    resetView()
    void (async () => {
      try {
        const signed = await getSignedUrl(doc.id)
        if (!signed) throw new Error(pt.operations.imageViewer.failed)
        if (!cancelled) setUrl(signed)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [doc, resetView])

  // Keyboard zoom while the viewer is open. Escape is already handled by Dialog.
  useEffect(() => {
    if (!doc) return
    function onKey(e: KeyboardEvent) {
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        setZoom((z) => zoomIn(z))
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        setZoom((z) => {
          const next = zoomOut(z)
          if (next === MIN_ZOOM) setPan({ x: 0, y: 0 })
          return next
        })
      } else if (e.key === '0') {
        e.preventDefault()
        resetView()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [doc, resetView])

  function stepOut() {
    setZoom((z) => {
      const next = zoomOut(z)
      // Snapping back to fit must also recentre, or the image stays parked off-screen
      // with no way to bring it back except closing the dialog.
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 })
      return next
    })
  }

  // Ctrl/⌘+wheel zooms; a bare wheel keeps scrolling the page, which is what every
  // other zoomable surface on the web does.
  function onWheel(e: React.WheelEvent) {
    if (!e.ctrlKey && !e.metaKey) return
    e.preventDefault()
    setZoom((z) => {
      const next = zoomByWheel(z, e.deltaY)
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 })
      return next
    })
  }

  const panning = zoom > MIN_ZOOM

  function onPointerDown(e: React.PointerEvent) {
    if (!panning) return
    drag.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  function onPointerMove(e: React.PointerEvent) {
    const d = drag.current
    if (!d) return
    setPan({ x: d.panX + (e.clientX - d.x), y: d.panY + (e.clientY - d.y) })
  }
  function onPointerUp(e: React.PointerEvent) {
    drag.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  const t = pt.operations.imageViewer

  return (
    <Dialog
      open={!!doc}
      onClose={closeImagePreview}
      title={
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{doc?.filename ?? ''}</span>
            {doc?.documentNumber && (
              /* O número impresso da fatura, quando a extracção leu um — identifica o documento
                 por aquilo que ele É, não só pelo nome do ficheiro. */
              <span
                className="shrink-0 font-mono text-[12px] font-medium text-gray-500"
                title={doc.documentNumber}
              >
                {doc.documentNumber}
              </span>
            )}
          </span>
          {url && (
            <div className="flex flex-shrink-0 items-center gap-1">
              <ZoomButton onClick={stepOut} label={t.zoomOut}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="20" y1="20" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </ZoomButton>
              <button
                type="button"
                onClick={resetView}
                title={t.reset}
                className="min-w-[52px] rounded-md px-1.5 py-1 font-mono text-[11.5px] font-semibold tabular-nums text-gray-600 hover:bg-gray-100"
              >
                {zoomLabel(zoom)}
              </button>
              <ZoomButton onClick={() => setZoom((z) => zoomIn(z))} label={t.zoomIn}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="20" y1="20" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                </svg>
              </ZoomButton>
              <button
                type="button"
                onClick={() => doc && void openSignedPreview(doc.id)}
                className="ml-1 flex items-center gap-1.5 rounded-md bg-brand-50 px-2.5 py-1 text-[12px] font-medium text-brand-800 hover:bg-brand-100"
              >
                {t.openTab}
              </button>
            </div>
          )}
        </div>
      }
      widthClassName="max-w-6xl"
    >
      {loading && <p className="py-8 text-center text-[13px] text-gray-500">{pt.common.loading}</p>}
      {error && <p className="py-8 text-center text-[13px] text-rose-600">{error}</p>}
      {url && (
        <div
          className="flex max-h-[72vh] min-h-[320px] items-center justify-center overflow-hidden rounded-md border border-DEFAULT bg-gray-50"
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ cursor: panning ? (drag.current ? 'grabbing' : 'grab') : 'default' }}
        >
          <img
            src={url}
            alt={doc?.filename ?? ''}
            draggable={false}
            onError={() => {
              // No browser-viewable derivative (pre-backfill HEIC/TIFF). Hand it to the
              // browser rather than showing a broken frame — same fallback the dashboard
              // thumbnail uses.
              setError(t.cannotRender)
              setUrl(null)
            }}
            className="max-h-[72vh] w-auto max-w-full select-none object-contain"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: drag.current ? 'none' : 'transform 120ms ease-out',
            }}
          />
        </div>
      )}
      {url && <p className="pt-2 text-center text-[11px] text-gray-400">{t.hint}</p>}
    </Dialog>
  )
}

function ZoomButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    >
      {children}
    </button>
  )
}
